/**
 * What confirming the reserve dialog will actually do, in one sentence.
 *
 * Pure and dependency-free so it can be unit tested: vitest here is node-only
 * and matches `src/**\/*.test.ts`, so wording left inline in the dialog's
 * `.tsx` would never be covered.
 *
 * It exists because the dialog used to say `Reserve 5` — a bare count of
 * MACHINES. Confirming also holds material, which that number never mentioned,
 * and the button was disabled whenever the count was zero, so a run whose
 * machines were all held but whose material was not offered no way to take the
 * material at all. Every consequence of the action is named here instead.
 */

export interface ReserveOutcome {
  /** Machines that will be booked. */
  bookable: number
  /** Of those, ones already booked at a different time and simply re-timed. */
  moving: number
  /** Material lines that will be held. */
  holdableLines: number
  /** Material lines stock cannot cover — reported, never partially held. */
  shortLines: number
  /** Machines already held for exactly this window; nothing to do for them. */
  alreadyHeld: number
  /** Machines that cannot be booked at all (busy, no run time). */
  blocked: number
}

/** Is there anything at all for the confirm button to do? */
export function hasWorkToDo(o: ReserveOutcome): boolean {
  return o.bookable > 0 || o.holdableLines > 0
}

function plural(n: number, one: string, many: string): string {
  return `${n} ${n === 1 ? one : many}`
}

/**
 * The sentence shown above the confirm button.
 *
 * Leads with what WILL happen, then what is deliberately left alone. Short
 * lines and blocked machines are stated here rather than only in their own
 * sections so the summary is never rosier than the list above it.
 */
export function reserveSummaryText(o: ReserveOutcome): string {
  const willDo: string[] = []
  if (o.bookable > 0) willDo.push(`book ${plural(o.bookable, 'machine', 'machines')}`)
  if (o.holdableLines > 0) {
    willDo.push(`hold ${plural(o.holdableLines, 'material line', 'material lines')}`)
  }

  const leftAlone: string[] = []
  if (o.alreadyHeld > 0) {
    leftAlone.push(`${plural(o.alreadyHeld, 'machine is', 'machines are')} already held`)
  }
  if (o.blocked > 0) {
    leftAlone.push(`${plural(o.blocked, 'machine', 'machines')} cannot be booked`)
  }
  if (o.shortLines > 0) {
    leftAlone.push(`${plural(o.shortLines, 'material line is', 'material lines are')} short`)
  }

  const tail = leftAlone.length > 0 ? ` Left as-is: ${leftAlone.join(', ')}.` : ''

  if (willDo.length === 0) {
    // Not a failure — usually everything is already correct. Say which.
    return leftAlone.length > 0
      ? `Nothing left to do here — ${leftAlone.join(', ')}.`
      : 'This run needs no equipment or material, so there is nothing to reserve.'
  }

  // Named explicitly, because "Confirming will book 5 machines" on a run that
  // is ALREADY booked reads as double-booking otherwise.
  const moving =
    o.moving > 0
      ? ` ${plural(o.moving, 'machine is', 'machines are')} already booked for a different time and will be moved, not duplicated.`
      : ''

  return `Confirming will ${willDo.join(' and ')}.${moving}${tail}`
}
